import {createOperationDescriptor, getRequest, graphql} from 'relay-runtime';
import type {IEnvironment, Snapshot} from 'relay-runtime';
import RelayEnvironment from '../../../RelayEnvironment';
import {getNotNullishNodes} from './NotNullPredicate';
import {inAdminOrder} from './projectStageUtil';
import type {StageProfile} from './projectStageUtil';

export type ProjectStage = {id: string; name: string; sortOrder: number | null; profile: StageProfile | null};

type StageEdges = {
	readonly edges?: ReadonlyArray<{
		readonly node: {
			readonly id: string;
			readonly name?: string | null;
			readonly sortOrder?: number | null;
			readonly profile?: string | null;
		} | null;
	} | null> | null;
} | null;

type ProjectStagesCompany = {readonly projectStages?: StageEdges} | null | undefined;

graphql`
	fragment ProjectStages_company on Company {
		projectStages(first: 1000) @connection(key: "Company_projectStages") {
			edges {
				node {
					id
					name
					sortOrder
					profile
				}
			}
		}
	}
`;

// Store-only: never fetched. appv2's query loads and retains this data.
export const ProjectStagesStoreQuery = graphql`
	query ProjectStagesStoreQuery {
		viewer {
			company {
				...ProjectStages_company @relay(mask: false)
			}
		}
	}
`;

/** The company's stages in admin order; empty edges are dropped and a missing profile reads as null. */
export const projectStagesOf = (company: ProjectStagesCompany): ProjectStage[] =>
	inAdminOrder(
		getNotNullishNodes(company?.projectStages?.edges).map(node => ({
			id: node.id,
			name: node.name ?? '',
			sortOrder: node.sortOrder ?? null,
			profile: (node.profile as StageProfile | null | undefined) ?? null,
		}))
	);

export const lookupProjectStages = (environment: IEnvironment): Snapshot =>
	environment.lookup(createOperationDescriptor(getRequest(ProjectStagesStoreQuery), {}).fragment);

export const projectStagesFromSnapshot = (snapshot: Snapshot): ProjectStage[] =>
	projectStagesOf((snapshot.data as {viewer?: {company?: ProjectStagesCompany} | null} | null | undefined)?.viewer?.company);

export const readProjectStages = (environment: IEnvironment = RelayEnvironment.getInstance()): ProjectStage[] =>
	projectStagesFromSnapshot(lookupProjectStages(environment));

/** The first stage (admin order) with the profile, else null. One store read per call; not for per-row use. */
export const stageWithProfile = (profile: StageProfile, environment?: IEnvironment): ProjectStage | null =>
	readProjectStages(environment).find(stage => stage.profile === profile) ?? null;

/**
 * The `Status` to track for a change to the stage with this id: its profile for a default stage, its name for a stage
 * without one, undefined when the stage is not in the store.
 */
export const stageTrackingStatus = (stageId: string, environment?: IEnvironment): string | undefined => {
	const stage = readProjectStages(environment).find(candidate => candidate.id === stageId);
	return stage?.profile ?? stage?.name;
};
